// <trust-badges> — responsive fade carousel for the trust-badge bar.
// Mobile: fades through 1 item at a time. Tablet: 2 at a time. Desktop: all items shown (static).
// Self-contained: no theme module dependencies.

class TrustBadges extends HTMLElement {
  connectedCallback() {
    // Capture the canonical item list once, then we re-group it per breakpoint.
    this.items = Array.from(this.querySelectorAll(".trust-badges__item"));
    if (this.items.length === 0) return;

    this.track = document.createElement("div");
    this.track.className = "trust-badges__track";
    this.replaceChildren(this.track);

    this.delay = Number(this.dataset.autoplayDelay) || 3500;
    this.activeIndex = 0;
    this.pages = [];

    // Breakpoints: <768 = 1 per page, 768–1023.98 = 2, >=1024 = all (single static page).
    this.mqTablet = window.matchMedia("(min-width: 768px) and (max-width: 1023.98px)");
    this.mqDesktop = window.matchMedia("(min-width: 1024px)");
    this.mqReduced = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.onBreakpoint = () => this.layout();
    this.mqTablet.addEventListener("change", this.onBreakpoint);
    this.mqDesktop.addEventListener("change", this.onBreakpoint);
    this.mqReduced.addEventListener("change", this.onBreakpoint);

    this.layout();
  }

  disconnectedCallback() {
    this.stop();
    this.mqTablet?.removeEventListener("change", this.onBreakpoint);
    this.mqDesktop?.removeEventListener("change", this.onBreakpoint);
    this.mqReduced?.removeEventListener("change", this.onBreakpoint);
  }

  get perView() {
    if (this.mqReduced.matches) return this.items.length; // show everything, no motion
    if (this.mqDesktop.matches) return this.items.length;
    if (this.mqTablet.matches) return 2;
    return 1;
  }

  // (Re)build pages for the current breakpoint and restart autoplay.
  layout() {
    this.stop();
    const perView = this.perView;
    const pageCount = Math.ceil(this.items.length / perView);

    this.track.replaceChildren();
    this.pages = [];
    for (let p = 0; p < pageCount; p++) {
      const page = document.createElement("div");
      page.className = "trust-badges__page";
      page.append(...this.items.slice(p * perView, p * perView + perView));
      this.track.append(page);
      this.pages.push(page);
    }

    this.toggleAttribute("data-static", pageCount <= 1);
    this.activeIndex = Math.min(this.activeIndex, pageCount - 1);
    this.show(this.activeIndex);

    if (pageCount > 1 && !this.mqReduced.matches) this.start();
  }

  show(index) {
    this.pages.forEach((page, i) => page.classList.toggle("is-active", i === index));
    this.activeIndex = index;
  }

  next() {
    this.show((this.activeIndex + 1) % this.pages.length);
  }

  start() {
    this.stop();
    this.timer = window.setInterval(() => this.next(), this.delay);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get("trust-badges")) {
  customElements.define("trust-badges", TrustBadges);
}
